import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, switchMap, tap } from 'rxjs';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { WartungsItemComponent } from './wartungsitem/wartungsitem.component';
import { ItemsMode, WartungsBaumNode } from '../wartung.types';
import { CommonModule } from '@angular/common';
import { ClarityModule } from '@clr/angular';

import { FormsModule } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { ConfirmService } from 'src/app/services/confirm.service';

@Component({
  selector: 'app-wartungsbaum',
  templateUrl: './wartungsbaum.component.html',
  styleUrls: ['./wartungsbaum.component.scss'],
  imports: [CommonModule, ClarityModule, WartungsItemComponent, MrTranslatePipe, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WartungsBaumComponent implements OnInit {
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly store = inject(BasicStoreService);
  private readonly api = inject(WartungApiService);
  private readonly confirmService = inject(ConfirmService);

  private readonly artItem = viewChild('art', { read: WartungsItemComponent });
  private readonly arbeitItem = viewChild('arbeit', { read: WartungsItemComponent });
  private readonly treeContainer = viewChild('treeContainer', { read: ElementRef });

  readonly otypid = input.required<number>();
  readonly #zuordnungen$ = toObservable(this.otypid).pipe(
    tap(() => (this.selectedBaumNode = undefined)),
    switchMap(otypid => this.api.getWartungsBaum(otypid))
  );
  readonly #zuordnungen = toSignal(this.#zuordnungen$);

  protected readonly baumTree = linkedSignal(() => {
    return this.#zuordnungen()?.reduce((map, z) => {
      const arr = map.get(z.artID);
      if (arr) arr.unshift(z);
      else map.set(z.artID, [z]);
      return map;
    }, new Map<number, WartungsBaumNode[]>());
  });

  //#region zuordnungen
  protected setInsert = signal(false);

  private _selectedBaumNode: WartungsBaumNode;
  protected get selectedBaumNode(): WartungsBaumNode {
    return this._selectedBaumNode;
  }
  protected set selectedBaumNode(value: WartungsBaumNode) {
    this._selectedBaumNode = value;
    this.setInsert.set(value && !value.ID);
    this.artItem().selectedID = value?.artID;
    this.arbeitItem().selectedID = value?.arbeitID;
    this.selectedFK = value ? (value.Priorität ?? this.rangeFK.at(-1)) : undefined;
  }
  //#endregion
  protected rangeFK: number[] = [];
  protected selectedFK: number;

  //#region load()
  ngOnInit(): void {
    const { einstellung: { optMINNumFK = 0, optMAXNumFK = 4 } = {} } = this.store.getComponentStore('einstellung');
    for (let fk = optMINNumFK; fk <= optMAXNumFK; fk++) {
      this.rangeFK.push(fk);
    }
  }
  //#endregion
  //#region baumnode()
  protected addBaumNode() {
    this.selectedBaumNode = {
      artID: this.artItem().selID,
      arbeitID: this.arbeitItem().selID,
    };
  }

  protected async saveBaumNode() {
    const artID = this.artItem().selID;
    const arbeitID = this.arbeitItem().selID;

    if (!artID || !arbeitID) {
      this.toastr.error(
        this.mrTranslate.transform('Wartungsart und Wartungsarbeiten müssen ausgewählt sein'),
        this.mrTranslate.transform('Ungültige Eingabe')
      );
      return;
    }

    if (
      (this.setInsert() || artID != this.selectedBaumNode.artID || arbeitID != this.selectedBaumNode.arbeitID) &&
      this.hasTreeNode({ artID, arbeitID })
    ) {
      this.toastr.error(
        this.mrTranslate.transform('Eintrag bereits existriert'),
        this.mrTranslate.transform('Ungültige Eingabe')
      );
      return;
    }

    const sendObj: WartungsBaumNode = {
      ID: this.selectedBaumNode.ID,
      artID,
      arbeitID,
      Priorität: this.selectedFK,
      artName: this.artItem().selName,
      arbeitName: this.arbeitItem().selName,
    };

    const { success, error, ID } = await firstValueFrom(this.api.setWartungsBaumNode(this.otypid(), sendObj));

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      const neuItem: WartungsBaumNode = { ...sendObj, ID };
      if (!this.setInsert()) this.deleteTreeNode(this.selectedBaumNode);
      this.addTreeNode(neuItem);
      this.selectedBaumNode = neuItem;
      setTimeout(() => {
        this.treeContainer()
          .nativeElement.querySelector('#w-' + this.selectedBaumNode.ID)
          ?.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
      });
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteBaumNode() {
    const text = this.mrTranslate.transform('Wartungsverknüpfung wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (!ok) return;

    const { success, error } = await firstValueFrom(this.api.deleteWartungsItem('baum-node', this.selectedBaumNode.ID));

    if (success) {
      this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
      this.deleteTreeNode(this.selectedBaumNode);
      this.selectedBaumNode = undefined;
    } else {
      this.toastr.error(error, this.mrTranslate.transform('Fehler beim Löschen'));
    }
  }
  //#endregion
  //#region tree()
  protected async editTree(mode: ItemsMode, { id, name }: { id: number; name: string }) {
    this.findTreeNode(mode, id).forEach(w => (w[mode + 'Name'] = name));
  }

  protected async cleanTree(mode: ItemsMode, id: number) {
    const zuordnungen = this.findTreeNode(mode, id);
    if (!zuordnungen.length) return;

    this.selectedBaumNode = undefined;

    zuordnungen.forEach(w => {
      firstValueFrom(this.api.deleteWartungsItem('baum-node', w.ID));
      if (mode == 'arbeit') this.deleteTreeNode(w);
    });

    if (mode == 'art') {
      this.baumTree().delete(id);
      this.updateTreeSignal();
    }
  }

  private findTreeNode(mode: string, id: number): WartungsBaumNode[] {
    return mode == 'art' ?
        (this.baumTree().get(id) ?? [])
      : Array.from(this.baumTree().values()).flatMap(wArr => wArr.filter(w => w.arbeitID == id));
  }

  private hasTreeNode({ artID, arbeitID }: WartungsBaumNode): boolean {
    const arr = this.baumTree().get(artID);
    return arr?.some(w => w.arbeitID == arbeitID);
  }

  private addTreeNode(w: WartungsBaumNode) {
    const arr = this.baumTree().get(w.artID);
    if (arr) arr.unshift(w);
    else this.baumTree().set(w.artID, [w]);
    this.updateTreeSignal();
  }

  private deleteTreeNode(w: WartungsBaumNode) {
    const arr = this.baumTree().get(w.artID);
    if (arr.length > 1) arr.splice(arr.indexOf(w), 1);
    else this.baumTree().delete(w.artID);
    this.updateTreeSignal();
  }

  private updateTreeSignal() {
    this.baumTree.update(tree => new Map(tree));
  }
  //#endregion
}
